'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

/**
 * Parallax hero with:
 * - "10,000+ Patients Agree" + 5 stars
 * - Big headline with the green "FeelGoodHealth did"
 * - Verified badge
 * - Center bottle that moves down as you scroll
 * - Real background quote divs (not an image)
 * - Two new floating UI components with parallax
 */
export default function Parallax() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [y, setY] = useState(0);

  useEffect(() => {
    // Respect reduced-motion
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) return;

    let raf = 0;

    const onScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const vh = window.innerHeight;

      // Progress: 0 when section top hits bottom of the viewport,
      // 1 when the section top reaches the top of the viewport.
      let p = 1 - rect.top / vh;
      // Keep the value tidy between 0 and ~1.2 (slightly more for continued movement)
      p = Math.max(0, Math.min(1.2, p));

      // Translate pixels – tweak the multiplier to taste (e.g., 140 → larger travel)
      const translateY = p * 120;

      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setY(translateY));
    };

    // Run once to position initially
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate overflow-hidden bg-white py-8 sm:py-12"
    >
      {/* Top copy */}
      <div className="mx-auto max-w-[1200px] px-4 text-center">
        <p className="text-xs font-semibold tracking-wider text-neutral-600">
          10,000+ Patients Agree
        </p>

        {/* Stars */}
        <div className="mt-3 flex items-center justify-center gap-0">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} />
          ))}
        </div>

        {/* Big headline */}
        <h2 className="mt-6 text-3xl font-bold leading-tight text-[#1e1b16] md:text-5xl">
          “When nothing else worked,&nbsp;
          <span className="text-[#ff7500] font-extrabold">FeelGood did</span>”
        </h2>

        {/* Verified chip */}
        <div className="mt-3 inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-bold text-[#1e1b16]">
          <svg
            viewBox="0 0 24 24"
            className="h-6 w-6 bg-[#ff7500] text-[#ffffff] rounded-full"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M9.55 16.1 5.9 12.45l1.4-1.4 2.25 2.25 6.2-6.2 1.4 1.4-7.6 7.6z" />
          </svg>
          Verified FeelGood Customer
        </div>
      </div>

      {/* Background quotes (real text elements) */}
      {/* <QuotesLayer /> */}

      {/* Center bottle and floating components with parallax */}
      <div className="pointer-events-none relative mx-auto mt-2 flex h-[54vh] max-h-[640px] min-h-[380px] w-full items-center justify-center sm:mt-4">

        {/* === LEFT FLOATING COMPONENT (Health Score) === */}
        <div
          className="absolute left-1/2 top-[20%] w-44 -translate-x-[130%] sm:w-56 md:w-64 will-change-transform"
          style={{
            // Moves *up* relative to the vial, which moves *down*
            // A negative multiplier on y provides the opposite movement (closer to the camera/faster)
            // The initial offset (-20) ensures it starts slightly higher.
            transform: `translate3d(0, ${-y * 0.5 - 20}px, 0)`,
            transition: 'transform 60ms linear',
          }}
        >
          {/* Note: In a real app, you would define these paths as constants */}
          <Image
            src="/images/ui-component-health-score.png"
            alt="UI component showing a Health Score of 72"
            width={260}
            height={154}
            priority
            className="h-auto w-full drop-shadow-xl"
          />
        </div>

        {/* === CENTER VIAL === */}
        <div
          className="relative will-change-transform"
          style={{
            // Vial moves down (y) and rotates slightly
            transform: `translate3d(0, ${y - 30}px, 0) rotate(15deg)`,
            transition: 'transform 60ms linear', // small smoothing
          }}
        >
          <Image
            src="/images/feelgood-vial.png" // <-- replace with your asset
            alt="FeelGoodHealth GLP-1 Medication bottle"
            width={200}
            height={200}
            priority
            className="h-auto w-[100px] sm:w-[120px] md:w-[120px] drop-shadow-2xl z-10"
          />
        </div>

        {/* === RIGHT FLOATING COMPONENT (Biomarkers) === */}
        <div
          className="absolute right-1/2 top-[40%] w-60 translate-x-[130%] sm:w-72 md:w-80 will-change-transform -z-1"
          style={{
            // Moves *up* relative to the vial, but at a slightly different speed
            // and with a different initial offset.
            transform: `translate3d(0, ${-y * 1.2 - 5}px, 0)`,
            transition: 'transform 60ms linear',
          }}
        >
          <Image
            src="/images/ui-component-range.png"
            alt="UI component showing biomarker data"
            width={320}
            height={340}
            priority
            className="h-auto w-full drop-shadow-xl rounded-lg"
          />
        </div>
      </div>

      {/* Soft bottom fade to separate next section, optional */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-white" />
    </section>
  );
}

/* ——— Helpers ——— */

function Star() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-10 w-10 text-[#ff7500]"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12 17.27 18.18 21 16.54 13.97 22 9.24l-7.19-.62L12 2 9.19 8.62 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );
}

function QuotesLayer() {
  // Simple grid of translucent quotes; adjust copy/positions freely.
  const quotes = [
    'Very easy and convenient for a very busy working lady.',
    '…the weight vanished!',
    'Every person I have contact with shows compassion.',
    'Very nice and informative Dr. Quick and easy!',
    'Everyone I’ve met was helpful and friendly!',
    'The company and doctors are amazing; they take care of everything.',
  ];

  return (
    <div
      className="
        pointer-events-none
        absolute inset-0 -z-10
        select-none
      "
      aria-hidden="true"
    >
      <div className="mx-auto grid h-full max-w-6xl grid-cols-1 gap-8 px-4 sm:grid-cols-2 md:grid-cols-3">
        {quotes.map((q, i) => (
          <div
            key={i}
            className="
              rounded
              text-center text-2xl font-extrabold
              leading-snug text-neutral-300/25
              sm:block
            "
            style={{
              // staggered vertical rhythm by index
              marginTop: i % 3 === 0 ? '2rem' : i % 3 === 1 ? '5rem' : '8rem',
            }}
          >
            {q}
            <div className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-neutral-300/40">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-neutral-300/40" />
              Verified FeelGoodHealth Customer
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}